import { IBaseTyreOffer, IThemeColors } from '@wyz/types';
import { isEmptyValue } from '@wyz/utils';
import { isNil, round, sumBy, uniqueId } from 'lodash-es';
import { PurchaseSearchHelpers, QUOTE_MIN_QUANTITY } from '~/purchase/helpers';
import {
  IBaseFilter,
  IBaseProductSearchQs,
  IBaseSearchFormSchema,
  IProductListState,
  IQuote,
  IQuoteAvailability,
  IQuoteCart,
  IQuoteCartAddServicePayload,
  IQuoteCartForm,
  IQuoteCartGroup,
  IQuoteCartItem,
  IQuoteCartSubmitPayload,
  IQuoteItemInfo,
  IQuoteListState,
  IQuoteProximityStock,
  IQuoteStock,
  IQuoteTyreOffer,
  ISeason,
  FilterFields,
  IQuoteCartStep,
} from '~/purchase/types';
import { QUOTE_CART_STEPS, SEARCH_TYPE_ENUM } from '~/purchase/schemas';

export function sortProducts(
  products: IQuoteTyreOffer[],
  listState: IQuoteListState,
) {
  const result = [...products];
  const sort = listState.sort;
  if (!sort || sort === PurchaseSearchHelpers.DEFAULT_SEARCH_SORT) {
    return result;
  }

  let getters: Array<{
    getter: (product: IQuoteTyreOffer) => number;
    asc: boolean;
  }> = [];
  if (sort === 'price') {
    getters = [
      {
        getter: (product) => {
          const price = product.suppliers.at(0)?.offers.at(0)?.displayPrice;
          return price ?? Infinity;
        },
        asc: true,
      },
    ];
  }

  return result.sort((product1, product2) => {
    for (const { getter, asc } of getters) {
      const diff = getter(product1) - getter(product2);
      if (diff !== 0 && !isNaN(diff)) {
        return asc ? diff : diff * -1;
      }
    }
    return 0;
  });
}

export function calculateBfDiscount(bf: number, priceTaxExcl: number): number {
  if (bf === 0) return 0;
  return round(((bf - priceTaxExcl) / bf) * 100, 2);
}

export function getPriceTaxIncl(
  priceTaxExcl: number,
  vatPercent: number,
): number {
  return round(priceTaxExcl * ((100 + vatPercent) / 100), 2);
}

export function getPriceTaxExcl(
  priceTaxIncl: number,
  vatPercent: number,
): number {
  return round((priceTaxIncl * 100) / (100 + vatPercent), 2);
}

export function getAllowedQuoteEditSteps(
  hasItems: boolean,
  hasServices: boolean,
  hasActiveServicePricingPolicy: boolean,
): Array<IQuoteCartStep> {
  if (!hasItems) {
    if (hasActiveServicePricingPolicy && hasServices) {
      return [QUOTE_CART_STEPS.SERVICES, QUOTE_CART_STEPS.CONTACT];
    }
    return [QUOTE_CART_STEPS.CONTACT];
  }
  return [
    QUOTE_CART_STEPS.PRODUCTS,
    hasServices && QUOTE_CART_STEPS.SERVICES,
    QUOTE_CART_STEPS.CONTACT,
  ].filter(Boolean) as Array<IQuoteCartStep>;
}

export function isSameDimension(
  item: IQuoteCartItem,
  dimensions: { width: number; height: number; diameter: number },
): boolean {
  return (
    item.tyre.width === dimensions.width &&
    item.tyre.height === dimensions.height &&
    item.tyre.diameter === dimensions.diameter
  );
}

export function getItemInfoFromTyreOffer(
  tyreOffer: IQuoteTyreOffer,
): IQuoteItemInfo {
  return {
    tyre: {
      ean: tyreOffer.tyre.ean,
      constructorReference: tyreOffer.tyre.constructorReference,
      alternativeReference: tyreOffer.tyre.alternativeReference,
      supplierCode: tyreOffer.tyre.supplierCode,
      height: tyreOffer.tyre.dimension.height,
      width: tyreOffer.tyre.dimension.width,
      diameter: tyreOffer.tyre.dimension.diameter,
      loadIndex: tyreOffer.tyre.dimension.loadIndexes.at(0) ?? 0,
      speedIndex: tyreOffer.tyre.dimension.speedIndexes.at(0) ?? '',
      profile: tyreOffer.tyre.profile.design1,
      dimension: tyreOffer.tyre.dimension.fullSize,
      brand: tyreOffer.tyre.brand.name,
      season: tyreOffer.tyre.season.name,
      homologations: tyreOffer.tyre.homologations,
      prodInfo: tyreOffer.tyre.prodInfo,
      vehicleTypePublicKey: tyreOffer.tyre.vehicleType.publicKey,
      vehicleType: tyreOffer.tyre.vehicleType.name,
      runFlat: tyreOffer.tyre.specification.runFlat,
      wetGrip: tyreOffer.tyre.labelling?.wetGrip,
      rollingResistance: tyreOffer.tyre.labelling?.rollingResistance,
      noisePerformance: tyreOffer.tyre.labelling?.noisePerformance,
      bf: tyreOffer.tyre.priceFrame.price,
      image: tyreOffer.tyre.pictures?.fullView,
    },
    proximityStocks: tyreOffer.proximityStocks,
    stocks: tyreOffer.suppliers
      .map(({ supplier, offers }) => {
        return offers.map(
          (offer) =>
            ({
              supplierId: supplier.id,
              supplierName: supplier.name,
              quantity: offer.availableQuantity,
              deliveryDate: offer.deliveryDate,
              offerType: offer.offerType,
              margin: offer.margin ?? 0,
              buyingPrice: offer.quoteBuyingPrice,
            }) satisfies IQuoteStock,
        );
      })
      .flat(),
    pricing: tyreOffer.quoteSelectedOffer
      ? {
          unitPriceTaxIncl: tyreOffer.quoteSelectedOffer.priceQTaxIncl,
          bfDiscount: tyreOffer.tyre.priceFrame.price
            ? tyreOffer.quoteSelectedOffer.discountPercentage
            : null,
          buyingPrice: tyreOffer.quoteSelectedOffer.buyingPrice,
          margin: tyreOffer.quoteSelectedOffer.margin,
          recyclingFee: tyreOffer.tyre.priceFrame.recyclingFee,
          currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
        }
      : undefined,
    availability: tyreOffer.quoteAvailability,
    inStockQuantity: tyreOffer.inStockQuantity,
  };
}

export const QUOTE_CART_MAX_DIMENSION_COUNT = 2;
export const QUOTE_CART_MAX_REFERENCE_COUNT = 4;

export function prepareSubmitCartPayload(
  data: IQuoteCartForm,
): IQuoteCartSubmitPayload {
  return {
    contact: data.contact,
    comment: data.comment,
    validityDays: data.validityDays,
    sendMail: false,
  };
}

export const getQuoteSearchQs = (
  cart: IQuoteCart,
  seasons?: Array<ISeason>,
): IBaseProductSearchQs => {
  const loadIndex = cart.items.at(0)?.tyre.loadIndex;
  const speedIndex = cart.items.at(0)?.tyre.speedIndex;
  const seasonPublicKey = seasons?.find(
    (season) => season.name === cart.items.at(0)?.tyre.season,
  )?.publicKey;
  return {
    quantity: QUOTE_MIN_QUANTITY,
    identifier: encodeURIComponent(cart.items.at(0)?.tyre.dimension ?? ''),
    fieldNameSearch: 'DIMENSION',
    width: cart.items.at(0)?.tyre.width,
    height: cart.items.at(0)?.tyre.height,
    diameter: cart.items.at(0)?.tyre.diameter,
    loadIndexes: loadIndex ? [loadIndex] : [],
    speedIndexes: speedIndex ? [speedIndex] : [],
    seasons: seasonPublicKey ? [seasonPublicKey] : [],
  };
};

export const hasMinimalQuoteSearchParams = (
  values: IBaseSearchFormSchema,
): boolean => {
  return (
    !isEmptyValue(values.identifier?.value) ||
    (!isEmptyValue(values.width) &&
      !isEmptyValue(values.height) &&
      !isEmptyValue(values.diameter) &&
      !isEmptyValue(values.quantity))
  );
};
export const STATUS_LOOKUP_VARIANT: Record<
  IQuote['computedStatus'],
  IThemeColors
> = {
  ACTIVE: 'success',
  EXPIRED: 'danger',
  DRAFT: 'warning',
};

export const getQuoteSearchDefaultValues = (
  overrides?: Partial<IBaseSearchFormSchema>,
) => ({
  ...PurchaseSearchHelpers.getTyreSearchDefaultValues(),
  ...overrides,
  quantity: overrides?.quantity ?? 2,
});

export const updatePrice = (
  cart: IQuoteCart,
  data: {
    items?: Array<{ id: string; price: number }>;
    services?: Array<{ id: string; price: number }>;
  },
) => {
  return {
    ...cart,
    items: cart.items.map((item) => ({
      ...item,
      unitPriceTaxIncl:
        data.items?.find((i) => i.id === item.id)?.price ??
        item.unitPriceTaxIncl,
    })),
    services: cart.services.map((service) => ({
      ...service,
      priceTaxIncl:
        data.services?.find((s) => s.id === service.id)?.price ??
        service.priceTaxIncl,
    })),
    groups: cart.groups.map((group) => ({
      ...group,
      items: group.items.map((item) => ({
        ...item,
        unitPriceTaxIncl:
          data.items?.find((i) => i.id === item.id)?.price ??
          item.unitPriceTaxIncl,
      })),
      services: group.services.map((service) => ({
        ...service,
        priceTaxIncl:
          data.services?.find((s) => s.id === service.id)?.price ??
          service.priceTaxIncl,
      })),
    })),
  };
};

export const addServiceToCart = (
  cart: IQuoteCart,
  data: IQuoteCartAddServicePayload,
): IQuoteCart => {
  const id = uniqueId();
  return {
    ...cart,
    groups: cart.groups.map((group) => {
      if (
        data.serviceType !== 'MOUNTING' ||
        !isNil(
          group.items.find((item) => data.targetEans?.includes(item.tyre.ean)),
        )
      ) {
        return {
          ...group,
          services: [
            ...group.services,
            {
              id: id,
              basePriceTaxIncl: data.priceTaxIncl,
              priceTaxIncl: data.priceTaxIncl,
              serviceCode: data.serviceCode,
              serviceName: data.serviceName,
              serviceType: data.serviceType,
              targetEans: data.targetEans || [],
            },
          ],
        };
      }
      return { ...group };
    }),
    services: [
      ...cart.services,
      {
        id: id,
        basePriceTaxIncl: data.priceTaxIncl,
        priceTaxIncl: data.priceTaxIncl,
        serviceCode: data.serviceCode,
        serviceName: data.serviceName,
        serviceType: data.serviceType,
        targetEans: data.targetEans || [],
      },
    ],
  };
};

export const removeServiceFromCart = (
  cart: IQuoteCart,
  data: { id: string },
): IQuoteCart => {
  return {
    ...cart,
    groups: cart.groups.map((group) => ({
      ...group,
      services: group.services.filter((service) => service.id !== data.id),
    })),
    services: cart.services.filter((service) => service.id !== data.id),
  };
};

export const removeItemFromCart = (cart: IQuoteCart, id: string) => {
  const item = cart.items.find((i) => i.id === id);
  if (item) {
    return {
      ...cart,
      items: cart.items.filter((item) => item.id !== id),
      services: cart.services.map((service) => ({
        ...service,
        targetEans: service.targetEans.filter((ean) => ean !== item.tyre.ean),
      })),
    };
  }
  return cart;
};

export const updateItemQuantity = (
  cart: IQuoteCart,
  id: string,
  body: { quantity: number },
) => {
  return {
    ...cart,
    items: cart.items.map((item) =>
      item.id === id ? { ...item, quantity: body.quantity } : item,
    ),
  };
};

export const calculateTotalTaxIncl = (group: IQuoteCartGroup) => {
  const itemsTotal = sumBy(
    group.items,
    (item) => item.unitPriceTaxIncl * item.quantity,
  );
  const servicesTotal = sumBy(
    group.services,
    (service) => service.priceTaxIncl,
  );
  return itemsTotal + servicesTotal;
};

export const calculateTotalTaxExcl = (group: IQuoteCartGroup) => {
  return calculateTotalTaxIncl(group) - group.vatPrice;
};

export const QUOTE_MIN_VALIDITY_DAYS = 1;
export const QUOTE_MAX_VALIDITY_DAYS = 60;
export const QUOTE_CART_MIN_QUANTITY = 1;
export const QUOTE_CART_MAX_QUANTITY = 4;

export const getFilterCount = <T extends FilterFields = FilterFields>(
  products: IBaseTyreOffer[],
  listState: IProductListState,
  filter: T,
  value: IBaseFilter[T],
): number => {
  let availability: IBaseFilter['availability'];
  // when no availability filter is selected, we force total and partial
  if (listState.filters.availability.length === 0) {
    availability = ['total', 'partial', 'unavailable'];
  } else {
    // if filter is not empty, we only keep total / partial values
    availability = listState.filters.availability.filter(
      (filter) =>
        filter === 'total' || filter === 'partial' || filter === 'unavailable',
    );
    // if total / partial is not selected, we return 0
    if (availability.length === 0 && filter !== 'availability') {
      return 0;
    }
  }

  return PurchaseSearchHelpers.filterProducts(
    products,
    {
      ...listState,
      filters: {
        ...listState.filters,
        availability,
        // generic filter must be after availability, so it will be overridden when we want to count availability
        [filter]: value,
      },
    },
    SEARCH_TYPE_ENUM.QUOTE,
  ).length;
};

export function computeAvailability(params: {
  requestedQuantity: number;
  inStockQuantity: number;
  availability: IQuoteAvailability;
  proximityStocks?: IQuoteProximityStock[];
}): IQuoteAvailability {
  const { requestedQuantity, inStockQuantity, availability, proximityStocks } =
    params;

  if (requestedQuantity <= inStockQuantity) {
    return 'IN_STOCK';
  }

  if (
    availability === 'UNAVAILABLE' &&
    proximityStocks &&
    proximityStocks.length > 0
  ) {
    return 'ORDERABLE';
  }

  return availability;
}

export function getInStockSupplierName(params: {
  availability: IQuoteAvailability;
  stocks?: IQuoteStock[];
  requestedQuantity: number;
}): string | undefined {
  const { availability, stocks, requestedQuantity } = params;

  if (availability !== 'IN_PARENT_STOCK' || !stocks || stocks.length === 0) {
    return undefined;
  }

  return stocks.find((stock) => stock.quantity >= requestedQuantity)
    ?.supplierName;
}
